import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareReply } from 'lucide-react';
import toast from 'react-hot-toast';
import { waysApi, type WaysDecideItem, type WaysQuestion } from '../../services/waysApi';

/**
 * Ways asks that need MY answer (hs-pmo/ep19#47), on the Ways page as "Needs my response" -- never
 * mixed with approvals, which are PMO's own list (hs-pmo/ep43#10). Each ask is a checklist of its
 * questions, answered inline; each submit is one reply in the thread, its audit. Holds are allowed
 * or refused here too. Notes and FYIs never appear: they stay in the channels.
 */
const GROUPS: { id: 'me' | 'my-project' | 'others'; title: string }[] = [
  { id: 'me', title: 'For me' },
  { id: 'my-project', title: 'For my projects' },
  { id: 'others', title: 'For others' },
];

export default function WaysAsks() {
  const [items, setItems] = useState<WaysDecideItem[] | null>(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => setTick((t) => t + 1), []);
  // Zane, 2026-10-29: by default only asks on me and on the projects I own; a super admin may view all.
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [canViewAll, setCanViewAll] = useState(false);
  const [othersCount, setOthersCount] = useState(0);

  useEffect(() => {
    let alive = true;
    waysApi.inboxDecide(scope)
      // Approvals are PMO's own list (the Approvals page); this is asks and holds only.
      .then((r) => {
        if (!alive) return;
        setItems((r.responses || r.items || []).filter((i) => i.type !== 'approval'));
        setCanViewAll(!!r.canViewAll);
        setOthersCount(r.othersCount || 0);
      })
      .catch(() => { if (alive) setItems(null); });
    return () => { alive = false; };
  }, [tick, scope]);

  // Esther (hs-ways-to-work/ep1#15): asks for me apart from asks for others. Holds and items from an
  // older Ways (no `for`) stay with mine.
  const grouped = GROUPS.map((g) => ({
    ...g, items: (items || []).filter((i) => (i.for || 'me') === g.id),
  })).filter((g) => g.items.length);

  return (
    // flex-1 min-h-0: bounded by the Ways pane, so the list scrolls here rather than also
    // pushing the page into a second scrollbar.
    <section className="flex-1 min-h-0 p-4 overflow-y-auto">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
          <MessageSquareReply className="w-4 h-4" /> Needs my response
        </h2>
        {canViewAll ? (
          <button type="button" onClick={() => setScope((s) => (s === 'all' ? 'mine' : 'all'))}
            aria-pressed={scope === 'all'} className="text-xs text-accent hover:underline">
            {scope === 'all' ? 'Show only mine' : `View all${othersCount ? ` (${othersCount} more)` : ''}`}
          </button>
        ) : null}
      </div>
      {items === null ? <p className="text-sm text-slate-500">Ways could not be reached.</p>
        : items.length === 0 ? <p className="text-sm text-slate-500">Nothing is waiting for your answer.</p> : null}
      <div className="space-y-4">
        {grouped.map((g) => (
          <div key={g.id}>
            {grouped.length > 1 || g.id !== 'me' ? (
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                {g.title} <span className="font-normal tabular-nums">{g.items.length}</span>
              </h3>
            ) : null}
            <ul className="space-y-2">
              {g.items.map((it) => (it.type === 'hold'
                ? <HoldRow key={it.id} it={it} onDone={load} />
                : <AskRow key={it.id} it={it} onDone={load} />))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function threadLink(id: string) {
  const m = /^(.+)#(\d+)$/.exec(id);
  return m ? `/ways?channel=${encodeURIComponent(m[1])}&seq=${m[2]}` : '/ways';
}

/** "6 days ago" from an ISO time; '' when unknown. */
function age(iso?: string | null) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return '';
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (m < 60) return m <= 1 ? 'just now' : `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? '1 hour ago' : `${h} hours ago`;
  const d = Math.round(h / 24);
  return d === 1 ? '1 day ago' : `${d} days ago`;
}

/** Who is asking: "ZW · an agent session · VS Code - site-builder" (the card), else what the item has. */
function asker(it: WaysDecideItem) {
  const f = it.card?.from;
  if (f) return [f.initials, f.kind, f.label || f.session].filter(Boolean).join(' · ');
  const legacy = typeof it.from === 'string' ? it.from : it.from?.label || it.from?.initials;
  return [legacy, it.fromLabel].filter(Boolean).join(' · ');
}

/**
 * The background (what the asker said around the questions), so a person can answer without
 * opening the channel (Zane, 2026-10-29: "i cannot provide a response wihtout details"). Long text
 * folds after a few lines.
 */
function Background({ text }: { text: string }) {
  const [all, setAll] = useState(false);
  const long = text.split('\n').length > 6 || text.length > 480;
  return (
    <div className="mb-2 rounded bg-slate-50 dark:bg-slate-800/60 px-2 py-1.5">
      <p className={`text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words ${!all && long ? 'line-clamp-6' : ''}`}>{text}</p>
      {long ? (
        <button type="button" onClick={() => setAll((x) => !x)} className="text-xs text-accent hover:underline">
          {all ? 'Show less' : 'Show all'}
        </button>
      ) : null}
    </div>
  );
}

function AskRow({ it, onDone }: { it: WaysDecideItem; onDone: () => void }) {
  const card = it.card;
  const fromCard = card?.questions?.length ? card.questions : null;
  const questions: WaysQuestion[] = fromCard || (it.questions?.length ? it.questions
    : [{ id: 'q1', text: it.headline || it.text || 'Reply', kind: 'text' }]);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [busy, setBusy] = useState(false);
  const open = questions.filter((q) => !q.answer);
  const about = card?.about?.display;
  const when = age(card?.askedAt);

  const submit = async () => {
    const answers = open.filter((q) => values[q.id] !== undefined && values[q.id] !== '')
      .map((q) => ({ id: q.id, value: values[q.id] }));
    if (!answers.length) { toast.error('Answer at least one question'); return; }
    setBusy(true);
    try { await waysApi.answerAsk(it.id, answers); toast.success('Answered'); onDone(); }
    catch (e) { toast.error((e as Error).message || 'Could not send the answers', { duration: 8000 }); }
    finally { setBusy(false); }
  };

  const field = 'w-full px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100';
  return (
    <li className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-slate-500 dark:text-slate-400 min-w-0">
          {asker(it) ? <>From <span className="text-slate-700 dark:text-slate-200">{asker(it)}</span></> : null}
          {about ? <> · about {card?.about?.item
            ? <Link to={`/item/${card.about.item}`} className="text-accent hover:underline">{about}</Link>
            : <span className="text-slate-700 dark:text-slate-200">{about}</span>}</> : null}
          {when ? <> · {when}</> : null}
        </span>
        <Link to={threadLink(it.id)} className="text-xs text-accent hover:underline shrink-0">Open the conversation</Link>
      </div>
      {card ? null : <p className="text-sm text-slate-900 dark:text-slate-100 mt-1">{it.headline}</p>}
      {card?.background ? <div className="mt-1.5"><Background text={card.background} /></div> : null}
      <ol className="space-y-2 mt-1">
        {questions.map((q) => (
          <li key={q.id} className="text-sm">
            <div className="text-slate-900 dark:text-slate-100 font-medium">{q.text}</div>
            {q.answer ? (
              <div className="text-xs text-green-700 dark:text-green-400">
                Answered: {String(q.answer.value)}{q.answer.by ? ` (${q.answer.by})` : ''}
              </div>
            ) : q.kind === 'yesno' ? (
              <div className="flex gap-3 text-xs mt-1">
                {[true, false].map((v) => (
                  <label key={String(v)} className="flex items-center gap-1">
                    <input type="radio" name={`${it.id}-${q.id}`} checked={values[q.id] === v}
                      onChange={() => setValues((x) => ({ ...x, [q.id]: v }))} /> {v ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
            ) : q.kind === 'choice' ? (
              // Choices are sentences ("(a) Recommended: web 2, api 2, ..."): shown in full, not in a dropdown.
              <fieldset className="mt-1 space-y-1">
                <legend className="sr-only">{q.text}</legend>
                {(q.options || []).map((o) => (
                  <label key={o} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
                    <input type="radio" className="mt-0.5" name={`${it.id}-${q.id}`} checked={values[q.id] === o}
                      onChange={() => setValues((x) => ({ ...x, [q.id]: o }))} />
                    <span className="break-words">{o}</span>
                  </label>
                ))}
              </fieldset>
            ) : (
              <textarea rows={2} className={`${field} mt-1`} value={String(values[q.id] ?? '')}
                onChange={(e) => setValues((x) => ({ ...x, [q.id]: e.target.value }))} />
            )}
          </li>
        ))}
      </ol>
      {open.length ? (
        <button type="button" disabled={busy} onClick={submit}
          className="mt-2 px-3 py-1 text-xs rounded-md bg-accent text-white disabled:opacity-50">Send answers</button>
      ) : null}
    </li>
  );
}

function HoldRow({ it, onDone }: { it: WaysDecideItem; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const decide = async (decision: 'allow' | 'refuse') => {
    const reason = decision === 'refuse' ? (window.prompt('Why refuse it?') || '') : '';
    if (decision === 'refuse' && !reason.trim()) return;
    setBusy(true);
    try {
      await waysApi.releaseHold(it.id, decision, reason || undefined);
      toast.success(decision === 'allow' ? 'Allowed' : 'Refused');
      onDone();
    } catch (e) { toast.error((e as Error).message || 'Could not decide it', { duration: 8000 }); }
    finally { setBusy(false); }
  };
  return (
    <li className="px-3 py-2 rounded-md border border-amber-200 dark:border-amber-800">
      <div className="text-sm text-slate-900 dark:text-slate-100">Held: {it.summary}</div>
      <div className="mt-1 flex gap-3 text-xs">
        <button type="button" disabled={busy} onClick={() => decide('allow')} className="text-green-700 hover:underline">Allow</button>
        <button type="button" disabled={busy} onClick={() => decide('refuse')} className="text-red-600 hover:underline">Refuse</button>
      </div>
    </li>
  );
}
